import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '@/utils/axiosInstance';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Search, ExternalLink, Trash2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import ConfirmDeleteModal from '@/components/ConfirmDeleteModal';

const UrlsActivity = () => {
  const { t } = useTranslation('admin');
  const { toast } = useToast();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [brands, setBrands] = useState([]);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });
  const [filters, setFilters] = useState({
    brand: '',
    search: '',
    date_from: '',
    date_to: '',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [pagination, setPagination] = useState({
    count: 0,
    page: 1,
    page_size: 20,
    total_pages: 1
  });

  useEffect(() => {
    fetchBrands();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchData();
    }, 300);
    return () => clearTimeout(timer);
  }, [filters, currentPage, itemsPerPage]);

  const fetchBrands = async () => {
    try {
      const response = await axiosInstance.get('/api/brands/');
      setBrands(response.data.brands || []);
    } catch (error) {
      console.error('Error fetching brands:', error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      // Add pagination params
      params.append('page', currentPage.toString());
      params.append('page_size', itemsPerPage.toString());

      const response = await axiosInstance.get(`/api/admin/activity/urls/?${params}`);
      setData(response.data.results || []);
      setPagination({
        count: response.data.count || 0,
        page: response.data.page || 1,
        page_size: response.data.page_size || itemsPerPage,
        total_pages: response.data.total_pages || 1
      });
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const truncateUrl = (url, maxLength = 50) => {
    if (!url) return '-';
    return url.length > maxLength ? url.substring(0, maxLength) + '...' : url;
  };

  const deleteUrl = async () => {
    const urlId = deleteModal.id;
    if (!urlId) return;
    setDeletingId(urlId);
    try {
      await axiosInstance.delete(`/api/admin/activity/urls/${urlId}/delete/`);
      setData(prev => prev.filter(item => item.id !== urlId));
      toast({ description: t('common.deleted') });
      setDeleteModal({ open: false, id: null });
    } catch (error) {
      console.error('Error deleting URL:', error);
      toast({ variant: 'destructive', description: t('common.deleteFailed') });
    } finally {
      setDeletingId(null);
    }
  };

  // Use server-side pagination
  const totalPages = pagination.total_pages;

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('activity.searchPlaceholder')}
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="pl-9"
          />
        </div>

        <Select
          value={filters.brand || 'all'}
          onValueChange={(v) => setFilters({ ...filters, brand: v === 'all' ? '' : v })}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder={t('urls.filterByBrand')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('urls.allBrands')}</SelectItem>
            {brands.map((brand) => (
              <SelectItem key={brand} value={brand}>
                {brand}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Input
          type="date"
          value={filters.date_from}
          onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
          className="w-[150px]"
        />

        <Input
          type="date"
          value={filters.date_to}
          onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
          className="w-[150px]"
        />

        <span className="text-sm text-muted-foreground self-center ml-auto text-right">
          {pagination.count} {t('activity.results')}
        </span>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[300px]">{t('urls.url')}</TableHead>
              <TableHead>{t('urls.ean')}</TableHead>
              <TableHead>{t('urls.brand')}</TableHead>
              <TableHead>{t('urls.productRef')}</TableHead>
              <TableHead>{t('activity.createdBy')}</TableHead>
              <TableHead>{t('activity.createdAt')}</TableHead>
              <TableHead className="w-[80px]">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8">
                  {t('common.loading')}
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  {t('activity.noData')}
                </TableCell>
              </TableRow>
            ) : (
              data.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline flex items-center gap-1 text-sm"
                    >
                      {truncateUrl(item.url)}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </TableCell>
                  <TableCell className="font-mono text-sm">{item.ean}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{item.brand}</Badge>
                  </TableCell>
                  <TableCell>
                    {item.shopify_id ? (
                      <span className="text-sm">{t('urls.shopify')} {parseInt(item.shopify_id)}</span>
                    ) : (
                      <span className="text-sm text-muted-foreground">{t('urls.custom')}</span>
                    )}
                  </TableCell>
                  <TableCell>{item.created_by || '-'}</TableCell>
                  <TableCell>{formatDate(item.created_at)}</TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteModal({ open: true, id: item.id })}
                      disabled={deletingId === item.id}
                      title={t('common.delete')}
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {pagination.count > 0 && (
        <div className="flex items-center justify-between mt-4 px-2">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">{t('pagination.rowsPerPage')}:</span>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="border rounded px-2 py-1 text-sm bg-background"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3"
            >
              {t('pagination.previous')}
            </Button>

            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              return (
                <Button
                  key={pageNum}
                  variant={currentPage === pageNum ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setCurrentPage(pageNum)}
                  className="w-9 h-9 p-0"
                >
                  {pageNum}
                </Button>
              );
            })}

            {totalPages > 5 && currentPage < totalPages - 2 && (
              <>
                <span className="px-2 text-muted-foreground">...</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCurrentPage(totalPages)}
                  className="w-9 h-9 p-0"
                >
                  {totalPages}
                </Button>
              </>
            )}

            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages || totalPages === 0}
              className="px-3"
            >
              {t('pagination.next')}
            </Button>
          </div>
        </div>
      )}

      <ConfirmDeleteModal
        open={deleteModal.open}
        onOpenChange={(open) => setDeleteModal({ open, id: open ? deleteModal.id : null })}
        onConfirm={deleteUrl}
        title={t('common.confirmDeleteTitle')}
        description={t('common.confirmDeleteDescription')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        loading={deletingId === deleteModal.id}
      />
    </div>
  );
};

export default UrlsActivity;
